import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, CheckCircle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link');
      navigate('/login');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: formData.newPassword
      });
      
      toast.success('Password reset successful!');
      setSuccess(true);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('expired')) {
        toast.error('Reset link has expired. Please request a new one.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="font-playfair text-2xl font-bold text-gray-900 mb-4">
            Password Reset Successful
          </h2>
          <p className="font-outfit text-gray-600 mb-6">
            Your password has been reset successfully. You can now log in with your new password.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-primary hover:bg-primary/90"
          >
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/login')}
          className="flex items-center text-primary hover:text-primary/80 mb-4 font-outfit"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Login
        </button>

        <Card className="p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-playfair text-3xl font-bold text-gray-900 mb-2">
              Reset Your Password
            </h1>
            <p className="font-outfit text-gray-600">
              Enter your new password below
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="newPassword" className="font-outfit">
                New Password
              </Label>
              <Input
                id="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
                required
                minLength={6}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="font-outfit">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Re-enter new password"
                required
                className="mt-2"
              />
            </div>

            <div className="pt-2">
              <p className="text-xs text-gray-500 font-outfit">
                Password must be at least 6 characters long
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 text-lg bg-primary hover:bg-primary/90"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
